/**
 * PlaybookDetailPage — /playbooks/:playbookId
 *
 * Header: playbook id, version, manifest stats (rules / exemplars /
 * episodes processed / cheap_eval breakdown), "Trigger cycle" admin
 * button, and a tabs bar.
 *
 * Tabs:
 *   1. Rules — DO + DON'T grids with per-rule actions, click-to-expand
 *      rule detail, and an inline confidence-vs-evidence summary.
 *   2. Exemplars — gallery of best-in-class creatives.
 *   3. Wiki — gemini-generated narrative (lazy-loaded).
 *   4. Activity — agent-decision + rule-validation timeline.
 *
 * Refuted hypotheses are shown as a collapsed card under the Rules tab.
 */

import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Activity,
  Star,
  BookOpen,
  History,
  AlertCircle,
  Route as RouteIcon,
} from 'lucide-react';
import { clsx } from 'clsx';
import { usePlaybookDetail } from './hooks/usePlaybookDetail';
import { useRuleActions } from './hooks/useRuleActions';
import { TacticalRuleCard } from './components/TacticalRuleCard';
import { TacticalRuleDetail } from './components/TacticalRuleDetail';
import { ExemplarGallery } from './components/ExemplarGallery';
import { RefutedSection } from './components/RefutedSection';
import { AgentActivityTimeline } from './components/AgentActivityTimeline';
import { PlaybookWiki } from './components/PlaybookWiki';
import { PersonaRoutingDecisionCard } from '@/pages/CreativeMemoryPage/components/PersonaRoutingDecisionCard';

type Tab = 'rules' | 'exemplars' | 'wiki' | 'activity' | 'routing';

export function PlaybookDetailPage() {
  const { playbookId } = useParams<{ playbookId: string }>();
  const {
    detail,
    loading,
    error,
    refresh,
    wiki,
    wikiLoading,
    wikiError,
    fetchWiki,
  } = usePlaybookDetail(playbookId);
  const {
    pendingRuleAction,
    triggerInFlight,
    lastActionMessage,
    archive,
    validate,
    trigger,
  } = useRuleActions(playbookId);

  const [tab, setTab] = useState<Tab>('rules');
  const [openRuleId, setOpenRuleId] = useState<string | null>(null);

  const doRules = useMemo(
    () =>
      (detail?.rules || []).filter(
        (r) => r.polarity === 'DO' && r.status !== 'archived',
      ),
    [detail],
  );
  const dontRules = useMemo(
    () =>
      (detail?.rules || []).filter(
        (r) => r.polarity === 'DONT' && r.status !== 'archived',
      ),
    [detail],
  );
  const archivedRules = useMemo(
    () => (detail?.rules || []).filter((r) => r.status === 'archived'),
    [detail],
  );

  // Slice the recent activity stream for the Persona Routing tab so the
  // user doesn't have to scroll through unrelated rule_validation /
  // mcp_tool events. Mirrors how /creative-memory/routing scopes itself.
  const routingDecisions = useMemo(
    () =>
      (detail?.recent_activity || []).filter(
        (it) => it.decision_type === 'persona_routing',
      ),
    [detail],
  );

  const onRuleClick = (rid: string) =>
    setOpenRuleId((cur) => (cur === rid ? null : rid));
  const onSelectTab = (t: Tab) => {
    setTab(t);
    if (t === 'wiki' && !wiki && !wikiLoading) fetchWiki();
  };

  if (!playbookId) {
    return (
      <div className="px-6 py-6 text-sm text-slate-500">
        No playbook id supplied.
      </div>
    );
  }

  return (
    <div className="px-6 py-6">
      {/* Breadcrumb */}
      <Link
        to="/playbooks"
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="w-3 h-3" />
        All playbooks
      </Link>

      {/* Header */}
      <header className="mt-3 mb-5 flex items-start justify-between gap-4 border-b border-slate-200 pb-5">
        <div className="min-w-0 flex-1">
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-800">
            <BookOpen className="w-5 h-5 text-brand-teal" />
            <span className="truncate">{playbookId}</span>
          </h1>
          {detail && (
            <p className="mt-1 text-xs text-slate-500">
              Bundle version <span className="font-mono">{detail.version}</span>
              {' · '}
              published {new Date(detail.manifest.published_at).toLocaleString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => refresh()}
            disabled={loading}
            className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-slate-300 disabled:opacity-50"
          >
            <RefreshCw className={clsx('w-3 h-3', loading && 'animate-spin')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => trigger()}
            disabled={triggerInFlight}
            className="flex items-center gap-1 rounded-md bg-brand-teal px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-teal/90 disabled:opacity-50"
          >
            <RefreshCw
              className={clsx('w-3 h-3', triggerInFlight && 'animate-spin')}
            />
            {triggerInFlight ? 'Scheduling…' : 'Trigger cycle'}
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
          <div>
            <p className="font-semibold">Failed to load playbook</p>
            <p className="mt-0.5 text-rose-700">{error}</p>
          </div>
        </div>
      )}

      {lastActionMessage && (
        <div className="mb-5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          {lastActionMessage}
        </div>
      )}

      {detail && (
        <>
          {/* Manifest stat strip */}
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
            <Stat label="DO rules" value={doRules.length} />
            <Stat label="DON'T rules" value={dontRules.length} />
            <Stat label="Exemplars" value={detail.exemplars.length} />
            <Stat
              label="Episodes processed"
              value={detail.manifest.episodes_processed}
            />
            <Stat
              label="Cheap eval"
              value={`${detail.manifest.cheap_eval_accepted}A · ${detail.manifest.cheap_eval_rejected}R · ${detail.manifest.cheap_eval_escalated}E`}
            />
            <Stat
              label="Cycle duration"
              value={`${detail.manifest.cycle_duration_s.toFixed(1)}s`}
            />
          </div>

          {/* Tabs */}
          <nav className="mb-5 flex border-b border-slate-200">
            <TabButton
              active={tab === 'rules'}
              onClick={() => onSelectTab('rules')}
              icon={<Activity className="w-3.5 h-3.5" />}
              label={`Rules (${doRules.length + dontRules.length})`}
            />
            <TabButton
              active={tab === 'exemplars'}
              onClick={() => onSelectTab('exemplars')}
              icon={<Star className="w-3.5 h-3.5" />}
              label={`Exemplars (${detail.exemplars.length})`}
            />
            <TabButton
              active={tab === 'wiki'}
              onClick={() => onSelectTab('wiki')}
              icon={<BookOpen className="w-3.5 h-3.5" />}
              label="Wiki"
            />
            <TabButton
              active={tab === 'routing'}
              onClick={() => onSelectTab('routing')}
              icon={<RouteIcon className="w-3.5 h-3.5" />}
              label={`Routing (${routingDecisions.length})`}
            />
            <TabButton
              active={tab === 'activity'}
              onClick={() => onSelectTab('activity')}
              icon={<History className="w-3.5 h-3.5" />}
              label={`Activity (${detail.recent_activity.length})`}
            />
          </nav>

          {/* Tab body */}
          {tab === 'rules' && (
            <RulesTab
              doRules={doRules}
              dontRules={dontRules}
              archivedRules={archivedRules}
              refuted={detail.refuted}
              openRuleId={openRuleId}
              pendingRuleId={pendingRuleAction}
              onRuleClick={onRuleClick}
              onArchive={(rid) => archive(rid)}
              onValidate={(rid) => validate(rid)}
            />
          )}
          {tab === 'exemplars' && (
            <ExemplarGallery
              exemplars={detail.exemplars}
              rules={detail.rules}
            />
          )}
          {tab === 'wiki' && (
            <PlaybookWiki
              markdown={wiki}
              loading={wikiLoading}
              error={wikiError}
            />
          )}
          {tab === 'routing' && (
            <div className="space-y-3">
              {routingDecisions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
                  <RouteIcon className="mx-auto w-8 h-8 text-slate-300" />
                  <p className="mt-3 text-sm font-medium text-slate-700">
                    No persona-routing decisions for this playbook yet
                  </p>
                  <p className="mt-1 max-w-md mx-auto text-xs text-slate-500">
                    Routing verdicts appear here once a creative-review
                    backfill runs with{' '}
                    <span className="font-mono">PERSONA_RANKER_MODE=llm</span>{' '}
                    and the deep agent emits AGENT_DECISION episodes for
                    this playbook.
                  </p>
                </div>
              ) : (
                routingDecisions.map((d) => (
                  <PersonaRoutingDecisionCard key={d.episode_id} decision={d} />
                ))
              )}
            </div>
          )}
          {tab === 'activity' && (
            <AgentActivityTimeline items={detail.recent_activity} />
          )}
        </>
      )}
    </div>
  );
}

interface RulesTabProps {
  doRules: ReturnType<typeof Object>; // CreativeMemoryRule[] — keep type loose to reduce import surface
  dontRules: ReturnType<typeof Object>;
  archivedRules: ReturnType<typeof Object>;
  refuted: ReturnType<typeof Object>;
  openRuleId: string | null;
  pendingRuleId: string | null;
  onRuleClick: (id: string) => void;
  onArchive: (id: string) => void;
  onValidate: (id: string) => void;
}

function RulesTab(props: RulesTabProps) {
  const {
    doRules,
    dontRules,
    archivedRules,
    refuted,
    openRuleId,
    pendingRuleId,
    onRuleClick,
    onArchive,
    onValidate,
  } = props as any; // narrowed at component boundary; structurally consistent with usePlaybookDetail's return

  const renderGrid = (rules: any[]) => {
    const openRule = openRuleId ? rules.find((r: any) => r.rule_id === openRuleId) ?? null : null;
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {rules.map((r: any) => (
            <TacticalRuleCard
              key={r.rule_id}
              rule={r}
              pending={pendingRuleId === r.rule_id}
              onClick={onRuleClick}
              onArchive={onArchive}
              onValidate={onValidate}
            />
          ))}
        </div>
        {/* Inline detail panel — only shown when the open rule belongs to this section */}
        {openRule && (
          <DetailPanel
            rule={openRule}
            onClose={() => onRuleClick(openRuleId!)}
          />
        )}
      </div>
    );
  };

  if (doRules.length + dontRules.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
        <Activity className="mx-auto w-8 h-8 text-slate-300" />
        <p className="mt-3 text-sm font-medium text-slate-700">
          No rules extracted yet
        </p>
        <p className="mt-1 max-w-md mx-auto text-xs text-slate-500">
          A Creative Memory cycle will populate this list. Trigger one with
          the button at the top right.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {doRules.length > 0 && (
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            DO ({doRules.length})
          </h2>
          {renderGrid(doRules)}
        </section>
      )}
      {dontRules.length > 0 && (
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-rose-700">
            DON'T ({dontRules.length})
          </h2>
          {renderGrid(dontRules)}
        </section>
      )}
      <RefutedSection refuted={refuted as any} />
      {archivedRules.length > 0 && (
        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Archived ({archivedRules.length})
          </h2>
          {renderGrid(archivedRules)}
        </section>
      )}
    </div>
  );
}

interface DetailPanelProps {
  rule: any;
  onClose: () => void;
}

function DetailPanel({ rule, onClose }: DetailPanelProps) {
  if (!rule) return null;
  return <TacticalRuleDetail rule={rule} onClose={onClose} />;
}

interface StatProps {
  label: string;
  value: number | string;
}

function Stat({ label, value }: StatProps) {
  return (
    <div className="rounded-md border border-slate-100 bg-white px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-0.5 text-base font-semibold tabular-nums text-slate-800">
        {value}
      </p>
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

function TabButton({ active, onClick, icon, label }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition',
        active
          ? 'border-brand-teal text-brand-teal'
          : 'border-transparent text-slate-500 hover:text-slate-700',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
